/**
 * Operations: three views of one question, "is anything stuck".
 *
 * Tabs rather than three pages, because the answer is one answer and a person
 * checking it should not have to remember three URLs — and because splitting
 * them was a listed rework trap (WP13, WP5's ops screen, OR-C2 are one screen).
 *
 *   Ledger   — which ledger rows still await bank evidence (what was here).
 *   Bank     — the nightly cash proof per account, and the movements nothing
 *              explains on either side.
 *   Unclaimed — bank credits nobody has claimed, and the record of each review.
 *   Debits   — bank debits nothing explains, and the explanations waiting for a second person.
 *   Returns  — money sent back that has not yet been seen leaving the bank.
 *   Delivery — money facts that never reached funds or books, and bank
 *              notifications we could not apply.
 *
 * Ledger <-> bank reconciliation (ops):
 *
 *   1. Import a bank-statement CSV for one concentration account. Lines are
 *      fingerprinted (re-imports are no-ops); inbound credits auto-match a
 *      pending deposit ONLY on an exact reference + amount + currency hit;
 *      outbound debits link to sent payouts by the rail's own identifier, never by text. Everything else
 *      lands in the manual attribution queue.
 *   2. Read the daily report: how many ledger rows still await bank evidence,
 *      how old the oldest is, and which payouts are stale.
 *   3. Managers can run the reconciliation sweep on demand after an import.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import OpsBankTab from './OpsBankTab';
import OpsDebitsTab from './OpsDebitsTab';
import OpsDeliveryTab from './OpsDeliveryTab';
import OpsUnclaimedTab from './OpsUnclaimedTab';
import OpsReturnsTab from './OpsReturnsTab';
import ReconciliationExport from './ReconciliationExport';
import StatusBadge from '../../components/StatusBadge';
import { FormAlert } from '../../components/forms';
import reconciliationService, {
  type PendingEntry,
  type ReconciliationSummary,
  type StatementFormat,
  type StatementImportResult,
} from '../../services/reconciliation.service';
import bankTransactionsService, { type ConcentrationAccountOption } from '../../services/bankTransactions.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';

export default function Reconciliation(): JSX.Element {
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [pending, setPending] = useState<PendingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const role = authService.getRole();
  const prefix = role === 'agent' ? '/agent' : '/manager';
  const [tab, setTab] = useState<TabKey>('ledger');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, p] = await Promise.all([reconciliationService.summary(), reconciliationService.pending()]);
      setSummary(s);
      setPending(p);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the reconciliation report.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const runSweep = async (): Promise<void> => {
    setRunning(true);
    setError(null);
    try {
      const r = await reconciliationService.run();
      setFlash(
        `Sweep done — deposits reconciled ${r.deposits_reconciled}, disputed ${r.deposits_disputed}, `
        + `withdrawals reconciled ${r.withdrawals_reconciled}, internal ${r.internal_reconciled}.`,
      );
      globalThis.setTimeout(() => setFlash(null), 8000);
      await reload();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not run the sweep.'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reconciliation"
        subtitle="Ledger entries against bank evidence: statement lines for deposits, executed payouts for withdrawals."
        actions={role !== 'agent' && tab === 'ledger' ? (
          <button type="button" onClick={() => { runSweep(); }} disabled={running}
                  className="px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: gradients.gold, color: colors.navy, opacity: running ? 0.6 : 1 }}>
            {running ? 'Running…' : 'Run sweep now'}
          </button>
        ) : undefined}
      />

      <TabBar active={tab} onChange={setTab} role={role} />

      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {tab === 'ledger' && (
        <>
        {summary && <SummaryTiles s={summary} payoutsHref={`${prefix}/ops/payouts`} />}

        <StatementImport onImported={() => { reload(); }} />

        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: colors.inkSoft }}>
            Awaiting bank evidence
          </h2>
          {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
          {!loading && pending.length === 0 && (
            <div className="rounded-2xl p-6 text-sm"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
              Every ledger entry is reconciled.
            </div>
          )}
          {!loading && pending.length > 0 && (
            <div className="overflow-x-auto rounded-2xl" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
              <table className="w-full text-sm">
                <thead style={{ background: colors.canvas, color: colors.muted }}>
                  <tr>
                    <th className="text-left font-medium px-4 py-3">Entry</th>
                    <th className="text-left font-medium px-4 py-3">Reference</th>
                    <th className="text-right font-medium px-4 py-3">Amount</th>
                    <th className="text-left font-medium px-4 py-3">Status</th>
                    <th className="text-left font-medium px-4 py-3">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((e, idx) => (
                    <tr key={e.ledger_entry_id} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}`,
                                                         background: e.reconciliation_status === 'disputed' ? colors.dangerBg : undefined }}>
                      <td className="px-4 py-3" style={{ color: colors.ink }}>
                        {ENTRY_TYPE_LABELS[e.entry_type]} · {e.category}
                        <div className="text-xs" style={{ color: colors.mutedSoft }}>
                          <Link to={`${prefix}/clients/${e.user_id}`} className="hover:underline">client</Link> · {formatDateTime(e.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>{e.reference_number ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>{formatMoney(e.amount, e.currency_code)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={e.reconciliation_status === 'disputed' ? 'danger' : 'warning'}>{RECONCILIATION_STATUS_LABELS[e.reconciliation_status]}</StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: e.age_days >= 3 ? colors.warning : colors.muted }}>{e.age_days}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <ReconciliationExport />
        </>
      )}

      {tab === 'bank' && <OpsBankTab />}
      {tab === 'unclaimed' && <OpsUnclaimedTab />}
      {tab === 'returns' && <OpsReturnsTab />}
      {tab === 'debits' && role !== 'agent' && <OpsDebitsTab />}
      {tab === 'delivery' && <OpsDeliveryTab canRedriveRows={role !== 'agent'} />}
    </>
  );
}

type TabKey = 'ledger' | 'bank' | 'unclaimed' | 'returns' | 'debits' | 'delivery';

const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: 'ledger', label: 'Ledger', hint: 'Rows still waiting for bank evidence' },
  { key: 'bank', label: 'Bank', hint: 'The nightly cash proof, per account' },
  { key: 'unclaimed', label: 'Unclaimed', hint: 'Bank credits nobody has claimed' },
  { key: 'returns', label: 'Returns', hint: 'Money sent back that has not been seen leaving' },
  { key: 'debits', label: 'Debits', hint: 'Bank debits nothing explains' },
  { key: 'delivery', label: 'Delivery', hint: 'Facts that never reached another system' },
];

function TabBar({ active, onChange, role }: Readonly<{
  active: TabKey;
  role: string | null;
  onChange: (key: TabKey) => void;
}>): JSX.Element {
  return (
    <div role="tablist" aria-label="Operations views"
         className="flex gap-1 mb-5 border-b" style={{ borderColor: colors.border }}>
      {TABS.filter((t) => t.key !== 'debits' || role !== 'agent').map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          title={t.hint}
          onClick={() => onChange(t.key)}
          className="px-4 py-2 text-sm font-semibold"
          style={{
            color: active === t.key ? colors.ink : colors.muted,
            borderBottom: `2px solid ${active === t.key ? colors.navy : 'transparent'}`,
            background: 'none',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** The words a person reads for a ledger row's kind and reconciliation state (never the stored values). */
const RECONCILIATION_STATUS_LABELS = { pending: 'pending', disputed: 'disputed' } as const;
const ENTRY_TYPE_LABELS = { credit: 'credit', debit: 'debit' } as const;

function SummaryTiles({ s, payoutsHref }: Readonly<{ s: ReconciliationSummary; payoutsHref: string }>): JSX.Element {
  const tiles = [
    { label: 'Ledger rows pending', value: s.pending, sub: s.pending ? `oldest ${s.pending_oldest_age_days}d` : 'all clear', warn: s.pending_oldest_age_days >= 3 },
    { label: 'Disputed', value: s.disputed, sub: 'amount / currency drift vs wire', warn: s.disputed > 0 },
    { label: 'Payouts open', value: s.payouts_open, sub: `${s.payouts_stale} stale (>${s.payout_stale_after_days}d)`, warn: s.payouts_stale > 0 },
    { label: 'Payouts failed / returned', value: s.payouts_failed_or_returned, sub: 'need reissue or reversal', warn: s.payouts_failed_or_returned > 0 },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-2xl p-4"
             style={{ background: colors.surface, border: `1px solid ${t.warn ? colors.warning : colors.border}` }}>
          <div className="text-xs" style={{ color: colors.muted }}>{t.label}</div>
          <div className="text-2xl font-bold" style={{ color: t.warn ? colors.warning : colors.ink }}>{t.value}</div>
          <div className="text-xs" style={{ color: colors.mutedSoft }}>
            {t.label.startsWith('Payouts') ? <Link to={payoutsHref} className="hover:underline">{t.sub}</Link> : t.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

/** What an import did, in one sentence, shown next to the button that did it. */
function importSummary(r: StatementImportResult): string {
  return (
    `Imported ${r.inserted} line(s), ${r.duplicates} duplicate(s) skipped, ${r.rejected.length} rejected, `
    + `${r.auto_matched_deposits} deposit(s) auto-matched, `
    + `${r.self_service_deposits} self-service transfer(s) matched, `
    + `${r.self_service_refused} left for manual attribution, `
    + `${r.auto_accepted_deposits} accepted automatically, `
    + `${r.payouts_linked} payout(s) linked.`
  );
}

function StatementImport({ onImported }: Readonly<{ onImported: (r: StatementImportResult) => void }>): JSX.Element {
  const [accounts, setAccounts] = useState<ConcentrationAccountOption[]>([]);
  const [accountId, setAccountId] = useState('');
  const [formats, setFormats] = useState<StatementFormat[]>([]);
  const [formatId, setFormatId] = useState('generic_csv');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<StatementImportResult['rejected']>([]);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    bankTransactionsService.listConcentrationAccounts()
      .then(setAccounts)
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load concentration accounts.')));
  }, []);

  // The bank/format adapters accepted for the chosen account's market — the
  // ingest half of the banking facade; a new bank is one adapter, not a UI change.
  useEffect(() => {
    const account = accounts.find((a) => a.id === accountId);
    if (!account) { setFormats([]); return; }
    reconciliationService.statementFormats(account.market)
      .then((rows) => {
        setFormats(rows);
        if (!rows.some((f) => f.format_id === formatId)) setFormatId(rows[0]?.format_id ?? 'generic_csv');
      })
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load statement formats.')));
  // formatId is intentionally not a dependency: this effect resets it when the account changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, accounts]);

  const submit = async (): Promise<void> => {
    if (!file || !accountId) return;
    setBusy(true);
    setError(null);
    setRejected([]);
    setDone(null);
    try {
      const r = await reconciliationService.importStatement(accountId, file, formatId);
      setRejected(r.rejected);
      setDone(importSummary(r));
      setFile(null);
      onImported(r);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Import failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: colors.inkSoft }}>Import bank statement (CSV)</h2>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Columns: <span className="font-mono">posted_at, amount, currency, direction, reference</span> (Spanish headers accepted;
        a signed amount works without <span className="font-mono">direction</span>). Re-importing the same statement is a no-op.
        Add <span className="font-mono">rail_trace</span> (the rail&apos;s own identifier: SPEI clave de rastreo, ACH trace, wire IMAD),{' '}
        <span className="font-mono">sender_account</span> on credits and <span className="font-mono">beneficiary_account</span> on
        debits whenever the bank gives them: a debit is tied to its payout by these, never by the reference text.
      </p>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Include the bank&apos;s closing balance in a <span className="font-mono">closing_balance</span> column, so the daily
        run can prove this account&apos;s cash against the bank. Without it the account is reported as not proved.
      </p>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
          <option value="">— concentration account —</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.market} · {a.currency} · {a.beneficiary}</option>
          ))}
        </select>
        <select value={formatId} onChange={(e) => setFormatId(e.target.value)} aria-label="Statement format"
                disabled={formats.length === 0}
                className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
          {formats.length === 0 && <option value="generic_csv">Generic CSV</option>}
          {formats.map((f) => (
            <option key={f.format_id} value={f.format_id}>{f.label}</option>
          ))}
        </select>
        <input type="file" accept=".csv,text/csv" aria-label="Statement CSV"
               onChange={(e) => setFile(e.target.files?.[0] ?? null)}
               className="text-sm" />
        <button type="button" onClick={() => { submit(); }} disabled={busy || !file || !accountId}
                className="px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ background: gradients.gold, color: colors.navy, opacity: busy || !file || !accountId ? 0.6 : 1 }}>
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
      {done && (
        <div className="mt-3">
          <FormAlert variant="success">{done}</FormAlert>
        </div>
      )}
      {rejected.length > 0 && (
        <ul className="mt-3 text-xs space-y-1" style={{ color: colors.danger }}>
          {rejected.map((r) => (
            <li key={r.line_no}>Line {r.line_no}: {r.reason}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  background: colors.canvas,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};
